import React, { useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { connect } from 'react-redux';
import { uniq } from 'lodash';
import { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

import {
  StatusChip,
  Typography,
  ProgressBar,
  FormInputWithButtonRow,
  AutocompleteOption,
  Autocomplete,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
} from '@occc-app/design-system';
import { State as RootState } from '@occc-app/rootRedux';
import { useAddMembershipToSegment, useRemoveMembershipFromSegment } from '@occc/feat-segments';
import { useChainId } from '@occc-app/Chain/hooks';

import { actions } from './redux';

import { CSVUploader, InstanceState as CSVUploaderInstanceState } from '../CSVUpload/CSVUploader';
import { actions as csvUploadActions, State as CSVUploadState } from '../CSVUpload/redux';
import { State as SegmentState } from '../../redux';
import { csvUploadStateToCopy } from '../../utils';

type Actions = typeof actions & typeof csvUploadActions;
type ReduxState = CSVUploadState & SegmentState;
type Props = ReduxState & Actions & { refetchSegments: () => void };

const SegmentMembershipAssignment: React.FC<Props> = (props) => {
  const [addMembershipToSegment] = useAddMembershipToSegment({
    refetchQueries: ['segment'],
  });
  const [removeMembershipFromSegment] = useRemoveMembershipFromSegment({
    refetchQueries: ['segment'],
  });
  const [selectedColumn, setSelectedColumn] = useState<AutocompleteOption | undefined>(undefined);

  const chainId = useChainId();

  const segmentId = props.segment.id;
  const { clearCSVUploadState, polledRequestCSVUploads } = props;

  useEffect(() => {
    clearCSVUploadState();
    polledRequestCSVUploads({ action: 'add_segment_memberships', chainId });
    // TODO: remove next comment and add all needed dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectedColumn = (event: React.ChangeEvent<{}>, value: AutocompleteOption) => {
    setSelectedColumn(value);
  };

  const additionalCSVUploadModalContext = (state: CSVUploaderInstanceState) => {
    const { columnNames, hasHeaders } = state;

    const availableColumns: AutocompleteOption[] = columnNames.map((columnName, i) => ({
      value: i.toString(),
      label: columnName,
    }));

    const headerInfoProvided = hasHeaders !== 'neither';
    const renderInput = (params: AutocompleteRenderInputParams): React.ReactNode => (
      <>
        <TextField
          {...params}
          label='Select Column that includes Membership IDs'
          fullWidth={true}
          variant='outlined'
          margin='normal'
          {...props}
        />
      </>
    );
    return (
      headerInfoProvided &&
      columnNames.length > 1 && (
        <Box px='1px'>
          <Autocomplete
            onChange={handleSelectedColumn}
            options={availableColumns}
            value={selectedColumn || null}
            renderInput={renderInput}
          />
        </Box>
      )
    );
  };

  const createSegmentMemberships = async (ids: string) => {
    const memberships = uniq(ids.split(',').map((id) => id.trim()));

    await addMembershipToSegment({
      variables: {
        input: {
          segmentId,
          memberships,
        },
      },
    });
  };

  const deleteSegmentMemberships = async (ids: string) => {
    const memberships = uniq(ids.split(',').map((id) => id.trim()));
    await removeMembershipFromSegment({
      variables: {
        input: {
          segmentId,
          memberships,
        },
      },
    });
  };

  const clearSelectedColumn = () => setSelectedColumn(undefined);

  const csvUploadModalDisableAction = (state: CSVUploaderInstanceState) => {
    const { columnNames } = state;
    return columnNames.length > 1 ? !selectedColumn : false;
  };

  const initiateCSVUpload = (state: CSVUploaderInstanceState) => {
    const action = 'addSegmentMemberships';
    const config = {
      segmentId,
      hasHeader: state.hasHeaders === 'yes',
      membershipColumnIndex: selectedColumn ? parseInt(selectedColumn.value, 10) : 0,
      refetchSegments: props.refetchSegments,
    };
    props.createCSVUpload(action, config, state.file!, chainId);
  };

  const renderProgressBar = (complete: boolean, percentage: number) => {
    if (complete) {
      return;
    }

    return <ProgressBar percentage={percentage || 0} />;
  };

  const renderTitle = () => (
    <Typography variant='h6' component='h3'>
      Assign Memberships to Segment
    </Typography>
  );

  const renderCsvUploads = () => {
    const { csvUploads } = props;

    if (csvUploads.length === 0) {
      return;
    }

    const csvUploadComponents = csvUploads.map((csvUpload) => {
      const complete = ['completed', 'failed'].includes(csvUpload.state);
      const percentage = !csvUpload.state ? 0 : csvUpload.processedCount / csvUpload.totalCount;

      return (
        <Box key={csvUpload.id} mb={2}>
          <Typography>
            {csvUpload.fileName} - {csvUploadStateToCopy(csvUpload.state)}
          </Typography>
          {renderProgressBar(complete, percentage)}
        </Box>
      );
    });

    return csvUploadComponents;
  };

  const { uploading } = props.segmentMemberships || {};
  const deleteConfirmDialogTitle = `Members with listed ID's will be removed from this segment. Are you sure you want to do that?`; // eslint-disable-line

  return (
    <Card>
      <CardHeader title={renderTitle()} />
      <CardContent>
        <Typography variant='subtitle1' paragraph>
          Segment is currently assigned to{' '}
          <StatusChip
            variant='success'
            label={!props.segment.membershipCount ? '--' : props.segment.membershipCount}
          />{' '}
          Memberships
        </Typography>
        <FormInputWithButtonRow
          label='Add one Membership ID at a time, or a comma separated list of up to 100 Membership IDs.'
          onAdd={createSegmentMemberships}
          onDelete={deleteSegmentMemberships}
          deleteConfirmDialog
          deleteConfirmDialogTitle={deleteConfirmDialogTitle}
        />
        <CSVUploader
          formLabelCopy='Or, upload a CSV with a Membership ID column to add members, remove members, or completely replace the members in a segment.'
          initiateCSVUpload={initiateCSVUpload}
          parentAdditionalModalContext={additionalCSVUploadModalContext}
          parentContextDisableAction={csvUploadModalDisableAction}
          parentResetModalState={clearSelectedColumn}
        />
        {renderCsvUploads()}
        {uploading && (
          <Box display='flex' justifyContent='center'>
            <CircularProgress />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

type MapStateToProps = (state: RootState) => ReduxState;

const mapStateToProps: MapStateToProps = ({ csvUpload: { csvUploads }, segment }) => {
  const myCSVUploads = csvUploads.filter(
    (csvUpload) =>
      csvUpload.action === 'addSegmentMemberships' &&
      csvUpload.config.segmentId === segment.segment.id,
  );
  return { ...segment, csvUploads: myCSVUploads };
};

const allActions: Actions = {
  ...actions,
  ...csvUploadActions,
};

export default connect(mapStateToProps, allActions)(SegmentMembershipAssignment);
